import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../../../core/ui';
import { useTranslation } from '../../../core/context/TranslationContext';
import { useFamily } from '../context/FamilyContext';
import styles from './ScopeSwitcher.module.css';

/**
 * ScopeSwitcher — segmented "Mine / Family" toggle.
 *
 * Replaces the previous duplication where users had to discover separate
 * sidebar entries for "Receipts" (own) and "Family → Receipts" (shared).
 * Now both views live under a single sidebar entry and the user picks
 * scope inline with a clearly labeled toggle.
 *
 * Hidden when the user has no family — keeps the personal pages clean
 * for solo users.
 *
 * Props:
 *   current      'mine' | 'family' — which view is being rendered
 *   mineRoute    path to navigate to for the "Mine" view (defaults below)
 *   familyRoute  path to navigate to for the "Family" view
 */
export default function ScopeSwitcher({ current, mineRoute, familyRoute }) {
  const { t } = useTranslation();
  const { family, loading } = useFamily();
  const navigate = useNavigate();

  if (loading || !family) return null;
  if (!mineRoute || !familyRoute) return null;

  const handlePick = (scope) => {
    if (scope === current) return;
    navigate(scope === 'mine' ? mineRoute : familyRoute);
  };

  return (
    <div className={styles.wrap} role="group" aria-label={t('scope.label')}>
      <button
        type="button"
        className={`${styles.btn} ${current === 'mine' ? styles.active : ''}`}
        onClick={() => handlePick('mine')}
        aria-pressed={current === 'mine'}
      >
        <Icon name="fa-user" size="sm" className={styles.icon} />
        <span>{t('scope.mine')}</span>
      </button>
      <button
        type="button"
        className={`${styles.btn} ${current === 'family' ? styles.active : ''}`}
        onClick={() => handlePick('family')}
        aria-pressed={current === 'family'}
      >
        <Icon name="fa-users" size="sm" className={styles.icon} />
        <span>{t('scope.family')}</span>
      </button>
    </div>
  );
}
